import { Link } from "react-router";
import { t } from "@wr/i18n";
import { StubPanel } from "@wr/ui";

/**
 * The client-routed 404 — and the `noindex` that makes its soft-404 honest.
 *
 * ── §16g A77 item 2, quoted because it is a ruling and not an oversight ─────
 *
 * > *"Soft-404s stay soft, and that is ruled, not overlooked. An SPA cannot know
 * > a route is invalid before its router runs, and pre-rendering a 404 list is a
 * > build-time route table that will drift from the router. `robots.txt` plus a
 * > `<meta name="robots" content="noindex">` emitted by the SPA's own not-found
 * > screen is the honest ceiling for a client-routed app. **Quinn: a 200 on
 * > `/does-not-exist` is accepted, do not re-file.**"*
 *
 * React 19 hoists a `<meta>` rendered anywhere in the tree into `<head>`, and
 * removes it again when this screen unmounts — so the tag is present for exactly
 * the routes that are 404s, and a crawler that follows a link off this screen to
 * a real one does not inherit it. That scoping is why it lives in the component
 * rather than in `index.html`, where it would `noindex` the entire product.
 */
export function NotFoundScreen() {
  return (
    <main
      className="wr-screen wr-stack"
      style={{ gap: 24, justifyContent: "center" }}
      data-testid="not-found"
    >
      <meta name="robots" content="noindex" />
      <div className="wr-form-column wr-stack" style={{ gap: 16 }}>
        <StubPanel title={t("notFound.title")} body={t("notFound.body")} testId="not-found-panel" />
        {/* Home is the splash, which routes wherever this session belongs — a
            404 must not have an opinion about where "back" is. */}
        <Link className="wr-btn wr-btn--primary wr-btn--md wr-btn--full" to="/">
          {t("notFound.home")}
        </Link>
      </div>
    </main>
  );
}
